import { useEffect, useMemo, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import map from 'lodash/map';
import each from 'lodash/each';
import sum from 'lodash/sum';

import { MemberData, MembersTextType } from '../../types/builder';
import FontLoader from "../../utils/FontLoader";
import {totalmem} from "os";
import builder from "../../../components/Builder";
import {first} from "lodash";
import { trackAddGamer, trackRemoveGamer } from '../../../utils/Analytics';

const blankMember: MemberData = {
  data: {
    size: 'XL',
    quantity: 1,
    sleeve: 'Short',
    sex: 'Male'
  },
  text: {
    title: {
      value: '',
      placeholder: 'Gamertag',
      enabled: false
    },
    fullname: {
      value: '',
      placeholder: 'Full Name',
      enabled: false
    },
    number: {
      value: '',
      placeholder: 'Number',
      enabled: false
    }
  },
  preview: '',
  model_preview:{
    front:"",
    back:"",
    right:"",
    left:""
  },
  modelType: '',
};

const textColors:MembersTextType = {
  title:'#ffffff',
  fullname:'#ffffff',
  number:'#ffffff'
}

const createBlankMember = (override = {}) => {
  return cloneDeep(merge(blankMember, override));
}

const useTeamMembers = function(controller, loadedData, options, isReady) {

  let searchParams:URLSearchParams = new URLSearchParams(window.location.search);

  const templateAction = loadedData?.action === "template";
  const editAction = searchParams.get("builder_action") === "edit" || loadedData?.action === "edit";

  // Preserve cart-edit params from ShareLoader (action, projectId, cartData).
  // These are stored in membersData by ShareLoader before URL params are stripped.
  const action = loadedData?.action || searchParams.get("builder_action") || null;
  const projectId = loadedData?.projectId || searchParams.get("project_id") || null;
  const cartData = loadedData?.cartData || searchParams.get("cart_data") || null;
  // rootSavedBuildId is set by useExporter during the cart flow
  const [rootSavedBuildId, setRootSavedBuildId] = useState<number|null>(null);

  const [price, setPrice] = useState(0);
  const [colorTheme, setColorTheme] = useState([]);
  const [quantity, setQuantity] = useState(0);
  const [members, setMembers] = useState<MemberData[]>(loadedData?.members && !templateAction ? loadedData?.members : [createBlankMember()]);
  const [currentMemberIndex, setCurrentMemberIndex] = useState(0);
  const [membersFontSize, setMembersFontSize] = useState<MembersTextType[]>( []);
  const [memberTextColors, setMemberTextColors] = useState<MembersTextType>(loadedData?.memberTextColors ?? textColors);
  const [memberFontFamily, setMemberFontFamily] = useState<string>("")
  const [currentMemberText, setCurrentMemberText] = useState(members[0].text);

  const defaultMemberSettings = () => {
    const gender = options?.gender;
    return  {
    preview: controller?.current?.cameraList?.back,
    data: {
      size: '',
      sleeve: loadedData && templateAction ? loadedData.members[0].data.sleeve : options?.sleeve ? "Short" : "Long",
      sex:gender && gender.length > 0 && gender[0].length > 0 ? String(gender[0])[0].toUpperCase() + String(gender[0]).slice(1) : ""
    },
    removeKey: '',
    messageBtn: 'Add item to cart',
    messageUnBtn: 'After adding this item to cart you will not be able to edit it again',
    resultAddedCart: false,
    index: currentMemberIndex,
  }};

  useEffect(() => {
    if (options && isReady && (!loadedData || loadedData && templateAction)) {
      setMembers([createBlankMember(defaultMemberSettings())]);
    }
    setMembersFontSize(loadedData?.membersFontSize ?? [createBlankMemberFontSize()]);
  }, [options, isReady])

  useEffect(()=>{
    if (controller.current) {
      controller?.current?.setBackroundCanvas(colorTheme);
    }
  },[colorTheme]);

  const currentMember = useMemo(() => members[currentMemberIndex], [currentMemberIndex, members]);

  const updateColor = (color) => {
    setColorTheme(prevState => {
      const state = [color];
      return state;
    })
  }

  const updateMember = (data, index = currentMemberIndex) => {
    setMembers(prevState => {
      const state = [...prevState];
      state[index] = merge(state[index], data);
      return state;
    })
    data?.text && setCurrentMemberText(prevState => ({...prevState, text:data.text}))
    const res = map(members,'data');
    const resT = map(res,'quantity');
    const resSum = sum(resT);
    setQuantity(resSum);
  };

  const totalSum = () => {
    return quantity;
  }

  const createBlankMemberFontSize = () => ({
    title:options?.positions?.title[4],
    fullname:options?.positions?.fullname[4],
    number:options?.positions?.number[4]
  })

  const updateMemberPreview = () => {
    if(controller?.current?.builderLayerComposer?.canvasHeight === 2048){
      controller?.current?.updateCameras({width:100, height:125}, ["back"],(cameraList) => updateMember({ preview: cameraList?.back }))
    }else{
      controller?.current?.updateCameras({width:100, height:125}, ["front"],(cameraList) => updateMember({ preview: cameraList?.front }))
    }
  }


  const addMember = () => {
    setMembers(prevState => {
      const state = [...prevState];
      state.push(createBlankMember({
        data: {
          size: '',
          sleeve: options?.sleeve ? "Short" : "Long"
        },
        removeKey: '',
        messageBtn: 'Add item to cart',
        messageUnBtn: 'After adding this item to cart you will not be able to edit it again',
        resultAddedCart: false,
        index: currentMemberIndex + 1,
      }));
      return state;
    });

    setMembersFontSize([...membersFontSize, createBlankMemberFontSize()]);
    setCurrentMemberIndex(members.length);
    updateMemberPreview();
    trackAddGamer();
  }

  const removeMember = (index) => {
    let resSum;
    setMembers(prevState => {
      const state = [...prevState];
      if (state.length > 1) {
        state.splice(index, 1);
      }

      if(index === currentMemberIndex) selectMember(0);
      if(index < currentMemberIndex) selectMember(currentMemberIndex-1);

      let res = map(state,'data');
      let resT = map(res,'quantity');
      resSum = sum(resT);
      trackRemoveGamer(index + 1);
      return state;
    });

    setMembersFontSize(prevState =>  {
      const state = [...prevState];
      state.splice(index, 1);
      return state;
    });

    setQuantity(resSum);
  }

  const selectMember = (index) => {
    if (members[index] && currentMemberIndex !== index) {
      setCurrentMemberIndex(index);
    }
    console.log('Переключился на футболку'+index);
  }

  const initMemberFonts = (font) => {
    if(!memberFontFamily){
      let defaultFont = loadedData?.memberFontFamily ?? font;
      defaultFont && FontLoader(defaultFont, (loadedFont) => setMemberFontFamily(loadedFont));
    }
  }

  const updateMemberFontSize = (textId,fontSize) => {
    setMembersFontSize(prevState => {
      const state = [...prevState];
      state[currentMemberIndex] = merge(state[currentMemberIndex], {[textId]:Math.round(fontSize)});
      return state;
    })
  }

  return {
    price,
    setPrice,
    colorTheme,
    setColorTheme,

    currentMemberIndex,
    currentMember,
    members,

    totalSum,
    addMember,
    removeMember,
    updateMember,
    updateColor,
    selectMember,

    membersFontSize,
    updateMemberFontSize,

    memberTextColors,
    setMemberTextColors,

    memberFontFamily,
    setMemberFontFamily,
    updateMemberPreview,
    currentMemberText,
    initMemberFonts,
    editAction,
    action,
    projectId,
    cartData,
    rootSavedBuildId,
    setRootSavedBuildId,
  }
}

export default useTeamMembers;